import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Link, TextField } from '@mui/material';
import { apiFetch, ApiError } from '../../lib/apiClient';
import PasswordField from '../../components/PasswordField';
import AuthLayout from './AuthLayout';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const initialEmail = (useLocation().state as { email?: string } | null)?.email ?? '';
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erreur inconnue.');
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await apiFetch<void>('/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) });
      setStep('reset');
    });
  };

  const reset = (e: FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    void run(async () => {
      await apiFetch<void>('/auth/reset', { method: 'POST', body: JSON.stringify({ email, code, password }) });
      navigate('/login', { replace: true, state: { passwordReset: true } });
    });
  };

  return (
    <AuthLayout title="Mot de passe oublié">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {step === 'email' ? (
        <Box component="form" onSubmit={sendCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="E-mail"
            type="email"
            autoComplete="username"
            autoFocus
            required
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy} sx={{ py: 1.25, mt: 1 }}>
            Recevoir un code
          </Button>
          <Link component={RouterLink} to="/login" variant="body2" sx={{ alignSelf: 'center' }}>
            Connexion
          </Link>
        </Box>
      ) : (
        <Box component="form" onSubmit={reset} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="E-mail" value={email} disabled fullWidth autoComplete="username" />
          <TextField
            label="Code"
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric' }}
            autoFocus
            required
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
          />
          <PasswordField
            label="Nouveau mot de passe"
            autoComplete="new-password"
            required
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordField
            label="Confirmation"
            autoComplete="new-password"
            required
            fullWidth
            value={confirm}
            error={mismatch}
              onChange={(e) => setConfirm(e.target.value)}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || mismatch} sx={{ py: 1.25, mt: 1 }}>
            Modifier le mot de passe
          </Button>
          <Link component="button" type="button" variant="body2" onClick={() => setStep('email')} sx={{ alignSelf: 'center' }}>
            Renvoyer un code
          </Link>
        </Box>
      )}
    </AuthLayout>
  );
}
